import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { FigmaCarCard } from '@/components/ui/figma-car-card'
import { AuthModal } from '@/components/common'
import { appRoutes } from '@/constants'
import { useMarketplaceListings } from '@/modules/cars/hooks/use-cars'
import { Loader2 } from 'lucide-react'

const FALLBACK_FEATURED_ITEMS = [
  {
    id: 'fc-1',
    slug: 'bmw-m4-competition',
    title: 'BMW M4 Competition',
    specText: '2024 · Brand New · xDrive',
    price: '₹98,40,000',
    image: 'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?q=90&w=800&auto=format&fit=crop',
    isSale: true,
  },
  {
    id: 'fc-2',
    slug: 'porsche-911-gt3',
    title: 'Porsche 911 GT3 RS',
    specText: '2023 · Pre-Owned · PDK',
    price: '₹2,45,00,000',
    image: 'https://images.unsplash.com/photo-1611821064430-0d40291d0f0d?q=90&w=800&auto=format&fit=crop',
    isSale: true,
  },
  {
    id: 'fc-3',
    slug: 'mercedes-amg-gt',
    title: 'Mercedes-AMG GT R',
    specText: '2022 · Certified · V8 Biturbo',
    price: '₹1,85,00,000',
    image: 'https://images.unsplash.com/photo-1544829099-b9a0c07fad1a?q=90&w=800&auto=format&fit=crop',
    isSale: true,
  },
  {
    id: 'fc-4',
    slug: 'audi-rs7-sportback',
    title: 'Audi RS7 Sportback',
    specText: '2023 · Brand New · Quattro',
    price: '₹1,45,00,000',
    image: 'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?q=90&w=800&auto=format&fit=crop',
    isSale: true,
  },
]

export function FeaturedCarsSection() {
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [wishlistedIds, setWishlistedIds] = useState<string[]>([])
  const [activeSlide, setActiveSlide] = useState(0)
  const navigate = useNavigate()

  const { data: marketplaceData, isLoading } = useMarketplaceListings({ limit: 8 })
  const realListings = marketplaceData?.items || []

  const displayCars = realListings.length > 0
    ? realListings.map((c: any) => ({
        id: String(c.id),
        slug: c.slug,
        title: c.title,
        specText: `${c.manufactureYear} · ${c.condition} · ${c.transmission}`,
        price: `₹${Number(c.askingPrice).toLocaleString('en-IN')}`,
        image: Array.isArray(c.images) && c.images[0]
          ? (typeof c.images[0] === 'string' ? c.images[0] : c.images[0].url)
          : 'https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?auto=format&fit=crop&w=1200&q=80',
        isSale: true,
      }))
    : FALLBACK_FEATURED_ITEMS

  const handleToggleWishlist = (id: string) => {
    if (wishlistedIds.includes(id)) {
      setWishlistedIds(wishlistedIds.filter((item) => item !== id))
    } else {
      setWishlistedIds([...wishlistedIds, id])
    }
  }

  const handleSelectCar = (item: any) => {
    navigate(`/cars/${item.slug || item.id}`)
  }

  return (
    <>
      <section id="featured-cars" className="py-12 relative bg-background border-t border-border/40">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Heading */}
          <div className="flex items-end justify-between">
            <div>
              <h2
                style={{
                  fontFamily: "'DM Sans', 'Poppins', sans-serif",
                  fontWeight: 800,
                  fontSize: '22px',
                  letterSpacing: '-0.01em',
                }}
                className="text-foreground"
              >
                FEATURED <span style={{ color: '#CF2F2F' }}>PRE-OWNED &amp; LUXURY CARS</span>
              </h2>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  marginTop: '4px',
                }}
                className="text-muted-foreground"
              >
                Inspected performance vehicles with clean titles &amp; verified service histories.
              </p>
            </div>
            <Link
              to={appRoutes.cars}
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: '12px',
                color: '#CF2F2F',
                textDecoration: 'none',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
              className="hover:underline"
            >
              VIEW ALL CARS ({realListings.length > 0 ? realListings.length : '10+'}) →
            </Link>
          </div>

          {/* 4 Cards Grid */}
          {isLoading ? (
            <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
              <Loader2 className="size-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading featured cars...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayCars.slice(0, 4).map((car: any, index: number) => (
                <motion.div
                  key={`${car.id}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                >
                  <FigmaCarCard
                    id={car.slug}
                    title={car.title}
                    specText={car.specText}
                    price={car.price}
                    image={car.image}
                    isSale={car.isSale}
                    isWishlisted={wishlistedIds.includes(car.id)}
                    onToggleWishlist={() => handleToggleWishlist(car.id)}
                    onSelect={() => handleSelectCar(car)}
                  />
                </motion.div>
              ))}
            </div>
          )}

          {/* Carousel Indicator Dots */}
          <div className="flex items-center justify-center gap-2 pt-2">
            {[0, 1, 2, 3].map((idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveSlide(idx)}
                style={{
                  backgroundColor: idx === activeSlide ? '#CF2F2F' : '#94a3b8',
                  width: idx === activeSlide ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '9999px',
                  transition: 'all 0.3s',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <AuthModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        initialMode="login"
        title="Sign In to Save Vehicle"
        description="Sign in or create an account to synchronize your wishlist across devices."
      />
    </>
  )
}


